import apiClient from './apiClient';
import { handleApiError, formatApiResponse } from './api';

/**
 * Servicio para gestión de configuraciones administrativas del sistema
 */
export const adminSettingsService = {
  // ============================================
  // CONFIGURACIONES GENERALES
  // ============================================

  /**
   * Obtener todas las configuraciones
   */
  getAllSettings: async (params = {}) => {
    try {
      const response = await apiClient.get('/admin/settings/', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener configuración por clave
   */
  getSettingByKey: async (key) => {
    try {
      const response = await apiClient.get(`/admin/settings/${key}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Crear nueva configuración
   */
  createSetting: async (data) => {
    try {
      const response = await apiClient.post('/admin/settings/', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Actualizar configuración
   */
  updateSetting: async (key, data) => {
    try {
      const response = await apiClient.put(`/admin/settings/${key}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Eliminar configuración
   */
  deleteSetting: async (key) => {
    try {
      const response = await apiClient.delete(`/admin/settings/${key}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ============================================
  // CONFIGURACIONES DE FACTURACIÓN
  // ============================================

  /**
   * Obtener configuraciones de facturación
   */
  getInvoicingSettings: async () => {
    try {
      const response = await apiClient.get('/admin/settings/invoicing/config');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Actualizar configuraciones de facturación
   */
  updateInvoicingSettings: async (data) => {
    try {
      const response = await apiClient.put('/admin/settings/invoicing/config', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ============================================
  // CONFIGURACIONES DE ÓRDENES DE COMPRA
  // ============================================

  /**
   * Obtener configuraciones de órdenes de compra
   */
  getPurchaseOrderSettings: async () => {
    try {
      const response = await apiClient.get('/admin/settings/purchase-orders/config');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Actualizar configuraciones de órdenes de compra
   */
  updatePurchaseOrderSettings: async (data) => {
    try {
      const response = await apiClient.put('/admin/settings/purchase-orders/config', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ============================================
  // CONFIGURACIONES DE EMAIL/SMTP
  // ============================================

  /**
   * Obtener configuraciones de email
   */
  getEmailSettings: async () => {
    try {
      const response = await apiClient.get('/admin/settings/email/config');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Actualizar configuraciones de email
   */
  updateEmailSettings: async (data) => {
    try {
      const response = await apiClient.put('/admin/settings/email/config', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Probar configuración de email
   */
  testEmailConfiguration: async (testEmail) => {
    try {
      const response = await apiClient.post('/admin/settings/email/test', null, {
        params: { test_email: testEmail }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ============================================
  // CONFIGURACIONES DEL SISTEMA
  // ============================================

  /**
   * Obtener configuraciones del sistema
   */
  getSystemSettings: async () => {
    try {
      const response = await apiClient.get('/admin/settings/system/config');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Actualizar configuraciones del sistema
   */
  updateSystemSettings: async (data) => {
    try {
      const response = await apiClient.put('/admin/settings/system/config', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ============================================
  // OPERACIONES ESPECIALES
  // ============================================

  /**
   * Actualización masiva de configuraciones
   */
  bulkUpdateSettings: async (settings) => {
    try {
      const response = await apiClient.post('/admin/settings/bulk-update', { settings });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Resetear configuraciones a valores por defecto
   */
  resetToDefaults: async (category = null) => {
    try {
      const params = category ? { category } : {};
      const response = await apiClient.post('/admin/settings/reset-defaults', null, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Inicializar configuraciones por defecto
   */
  initializeSettings: async () => {
    try {
      const response = await apiClient.post('/admin/settings/initialize');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

export default adminSettingsService;
